import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { CONFIG } from '../config';

const PREFERENCE_MAP = {
  'sms': ['All', 'SMS'],
  'whatsapp': ['All', 'WhatsApp'],
  'both': ['All', 'SMS', 'WhatsApp'],
};

const BulkAddGuestsPage = () => {
  const [parsedGuests, setParsedGuests] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const normalizePreference = (value) => {
    if (!value) return null;
    const lower = value.toString().trim().toLowerCase();
    if (lower === 'sms') return 'sms';
    if (lower === 'whatsapp') return 'whatsapp';
    if (lower === 'both' || lower === 'sms & whatsapp' || lower === 'sms and whatsapp' || lower === 'sms/whatsapp') return 'both';
    return null;
  };

  const processRows = (rows) => {
    if (!rows || rows.length === 0) {
      setParseError('File is empty or has no data rows.');
      return;
    }

    // Try to find columns by header name (case-insensitive)
    const headers = rows[0];
    let nameIdx = -1, phoneIdx = -1, prefIdx = -1, groupIdx = -1;

    if (headers && Array.isArray(headers)) {
      headers.forEach((h, i) => {
        const lower = (h || '').toString().trim().toLowerCase();
        if (lower === 'name' || lower === 'guest name' || lower === 'full name') nameIdx = i;
        if (lower === 'phone' || lower === 'phone number' || lower === 'phone_number' || lower === 'mobile') phoneIdx = i;
        if (lower === 'preference' || lower === 'message preference' || lower === 'message_preference' || lower === 'channel') prefIdx = i;
        if (lower === 'group' || lower === 'groups' || lower === 'event group' || lower === 'side') groupIdx = i;
      });
    }

    // If headers weren't detected, assume first row is data and columns are in order
    let dataRows;
    if (nameIdx >= 0 && phoneIdx >= 0 && prefIdx >= 0) {
      dataRows = rows.slice(1); // Skip header row
    } else {
      // Assume columns are: Name, Phone Number, Message Preference, Group
      nameIdx = 0;
      phoneIdx = 1;
      prefIdx = 2;
      groupIdx = 3;
      dataRows = rows; // First row might be data
    }

    const guests = [];
    const errors = [];

    dataRows.forEach((row, i) => {
      if (!row || row.every(cell => !cell || cell.toString().trim() === '')) return; // Skip empty rows

      const name = (row[nameIdx] || '').toString().trim();
      const phone = (row[phoneIdx] || '').toString().trim();
      const prefRaw = (row[prefIdx] || '').toString().trim();
      const pref = normalizePreference(prefRaw);
      const groupRaw = groupIdx >= 0 ? (row[groupIdx] || '').toString().trim() : '';

      if (!name) {
        errors.push(`Row ${i + 1}: Missing name`);
        return;
      }
      if (!phone) {
        errors.push(`Row ${i + 1}: Missing phone number`);
        return;
      }
      if (!pref) {
        errors.push(`Row ${i + 1}: Invalid message preference "${prefRaw}" (use SMS, WhatsApp, or Both)`);
        return;
      }

      // Build groups: "All" + preference groups + event group (if provided)
      const groupsList = [...PREFERENCE_MAP[pref]];
      if (groupRaw) {
        // Support multiple groups separated by commas or semicolons
        const eventGroups = groupRaw.split(/[,;]/).map(g => g.trim()).filter(Boolean);
        eventGroups.forEach(g => {
          if (!groupsList.includes(g)) {
            groupsList.push(g);
          }
        });
      }

      guests.push({
        name,
        phone_number: phone,
        preference: pref,
        preferenceLabel: prefRaw,
        eventGroup: groupRaw || '-',
        groups: groupsList,
      });
    });

    if (errors.length > 0 && guests.length === 0) {
      setParseError(`Could not parse any valid rows:\n${errors.join('\n')}`);
      return;
    }

    if (errors.length > 0) {
      setParseError(`${errors.length} row(s) skipped:\n${errors.join('\n')}`);
    }

    setParsedGuests(guests);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setParseError(null);
    setParsedGuests([]);
    setResults(null);

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      Papa.parse(file, {
        complete: (result) => {
          processRows(result.data);
        },
        error: (err) => {
          setParseError(`CSV parse error: ${err.message}`);
        },
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const workbook = XLSX.read(evt.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          processRows(rows);
        } catch (err) {
          setParseError(`Excel parse error: ${err.message}`);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setParseError('Unsupported file type. Please use .csv, .xlsx, or .xls');
    }
  };

  const handleUpload = async () => {
    if (parsedGuests.length === 0) return;

    try {
      setUploading(true);
      setResults(null);

      const payload = {
        guests: parsedGuests.map(g => ({
          name: g.name,
          phone_number: g.phone_number,
          groups: g.groups,
        })),
      };

      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/bulk_add_guests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await resp.json();

      if (!resp.ok) {
        setResults({ success: false, error: data.error || data.detail || 'Upload failed' });
      } else {
        setResults({ success: true, data });
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      setResults({ success: false, error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setParsedGuests([]);
    setFileName('');
    setParseError(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="admin-page">
      <h2 className="page-title">Bulk Add Guests</h2>
      <p className="page-subtitle">
        Upload a CSV or Excel file to add multiple guests at once. Each phone number will be validated via Twilio.
      </p>

      <div className="settings-section">
        <h3>Upload Guest List</h3>

        <div className="form-group">
          <label className="form-label">File (CSV or Excel)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="form-input"
            disabled={uploading}
          />
          <div className="input-hint">
            Expected columns: <strong>Name</strong>, <strong>Phone Number</strong>, <strong>Message Preference</strong> (SMS, WhatsApp, or Both), <strong>Group</strong> (e.g. Bride, Groom - optional)
          </div>
        </div>

        {parseError && (
          <div className="error-message" style={{ whiteSpace: 'pre-wrap' }}>
            {parseError}
          </div>
        )}

        {parsedGuests.length > 0 && !results && (
          <>
            <div className="form-group">
              <label className="form-label">
                Preview ({parsedGuests.length} guest{parsedGuests.length !== 1 ? 's' : ''} from {fileName})
              </label>
              <div style={{ overflowX: 'auto' }}>
                <table className="guest-preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Phone Number</th>
                      <th>Preference</th>
                      <th>Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedGuests.map((g, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{g.name}</td>
                        <td>{g.phone_number}</td>
                        <td>{g.preference === 'both' ? 'SMS & WhatsApp' : g.preference === 'sms' ? 'SMS' : 'WhatsApp'}</td>
                        <td>{g.eventGroup}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="form-group">
              <button
                onClick={handleUpload}
                className="btn btn-primary"
                disabled={uploading}
              >
                {uploading ? 'Uploading & Validating...' : `Upload ${parsedGuests.length} Guest${parsedGuests.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}

        {results && (
          <div className={`response-box ${results.success ? 'success' : 'error'}`}>
            {results.success ? (
              <>
                <strong>Upload Complete</strong>
                <div style={{ marginTop: '10px' }}>
                  <p>Total processed: {results.data.summary.total}</p>
                  <p style={{ color: '#16a34a' }}>Successfully added: {results.data.summary.added}</p>
                  {results.data.summary.updated > 0 && (
                    <p style={{ color: '#2563eb' }}>Updated (groups overwritten): {results.data.summary.updated}</p>
                  )}
                  {results.data.summary.invalid > 0 && (
                    <p style={{ color: '#dc2626' }}>Invalid/failed: {results.data.summary.invalid}</p>
                  )}
                </div>

                {results.data.failed && results.data.failed.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <strong>Failed Rows:</strong>
                    <div style={{ overflowX: 'auto', marginTop: '8px' }}>
                      <table className="guest-preview-table">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.data.failed.map((f, i) => (
                            <tr key={i}>
                              <td>{f.row}</td>
                              <td>{f.name}</td>
                              <td>{f.phone_number}</td>
                              <td>{f.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <strong>Upload Failed</strong>
                <p style={{ marginTop: '10px' }}>{results.error}</p>
              </>
            )}
          </div>
        )}

        {(parsedGuests.length > 0 || results) && (
          <button onClick={handleReset} className="btn btn-reset" style={{ marginTop: '16px' }}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default BulkAddGuestsPage;
